import { z } from 'zod';

import { ancillaryCabinOf } from '../../flight_ancillary/views';
import type { TravelerPolicyProfileWire } from './traveler_policy_profile';

/** A cabine no vocabulário da busca (`POST /flight/search`, `cabin`). */
const SearchCabinSchema = z.enum(['economy', 'premium', 'business', 'first']);
type SearchCabin = z.infer<typeof SearchCabinSchema>;

/**
 * As cabines que a política do viajante permite, no vocabulário da busca (#6438):
 * o que o seletor de cabine da busca aérea lê para marcar a classe fora da política.
 * `minDurationHours` = só em voos a partir de N horas (o `durationLimit` da policy-api).
 */
export const FlightCabinPolicySchema = z
  .object({
    allowed: z.array(
      z
        .object({ cabin: SearchCabinSchema, minDurationHours: z.number().positive().optional() })
        .strict(),
    ),
  })
  .strict();
export type FlightCabinPolicy = z.infer<typeof FlightCabinPolicySchema>;

/** O nome da policy-api (`ECONOMY`, `ECONOMY_PREMIUM`, `EXECUTIVE`, `FIRST_CLASS`) →
 *  a cabine da busca; `null` = nome que não reconhecemos. */
function searchCabinOf(name: string | null | undefined): SearchCabin | null {
  const cabin = ancillaryCabinOf(name);
  return cabin === 'premium_economy' ? 'premium' : cabin;
}

/**
 * O perfil de política → as cabines permitidas. `null` (fail-open: o seletor não marca
 * nada) quando o perfil não traz classe reconhecível — uma lista vazia marcaria TODAS
 * as cabines como fora da política por falta de dado.
 *
 * Uma exceção de rota que LIBERA uma classe (`countryExceptions`/`airportExceptions`
 * com `allowed`) torna essa classe permitida sem condição: a busca não sabe o país dos
 * aeroportos, e dizer "fora da política" de uma classe que a rota libera é pior que
 * não dizer nada.
 */
export function flightCabinPolicyOf(profile: TravelerPolicyProfileWire): FlightCabinPolicy | null {
  const byCabin = allowedByCabin(profile.flight?.allowedClasses ?? []);
  if (byCabin.size === 0) return null;
  for (const cabin of releasedByException(profile.flight)) byCabin.set(cabin, undefined);
  return {
    allowed: [...byCabin].map(([cabin, hours]) => ({
      cabin,
      ...(hours === undefined ? {} : { minDurationHours: hours }),
    })),
  };
}

type Flight = NonNullable<TravelerPolicyProfileWire['flight']>;

/** Cabine → horas mínimas de voo (`undefined` = sem condição). */
function allowedByCabin(
  classes: NonNullable<Flight['allowedClasses']>,
): Map<SearchCabin, number | undefined> {
  const byCabin = new Map<SearchCabin, number | undefined>();
  for (const entry of classes) {
    const cabin = searchCabinOf(entry.class);
    if (!cabin) continue;
    const hours = entry.durationLimit && entry.durationLimit > 0 ? entry.durationLimit : undefined;
    // A mesma cabine duas vezes: vale a regra mais solta (sem condição vence).
    const seen = byCabin.get(cabin);
    const looser = seen === undefined || hours === undefined ? undefined : Math.min(seen, hours);
    byCabin.set(cabin, byCabin.has(cabin) ? looser : hours);
  }
  return byCabin;
}

/** As cabines que alguma exceção de rota LIBERA. */
function releasedByException(flight: Flight | undefined): SearchCabin[] {
  const exceptions = [...(flight?.countryExceptions ?? []), ...(flight?.airportExceptions ?? [])];
  return exceptions
    .filter((exception) => exception.allowed === true)
    .flatMap((exception) => (exception.classes ?? []).flatMap((name) => searchCabinOf(name) ?? []));
}
